import React, { useState, useCallback, forwardRef, useEffect } from 'react';
import { CreateTableNew as CreateTable } from '../../../../../common/APICommon';
import { LoadingSpinner } from '../../../../../common/Loading';
import { useEmployeeLeaveBalance } from '../../../../hooks/useEmployeeLeaveBalance';
import EditIcon from '@mui/icons-material/Edit';
import LeaveCreditsTabAddEditModal from './components/LeaveCreditsTabAddEditModal';
import { isCellExitEditModeKeys } from '@mui/x-data-grid/utils/keyboardUtils';

export const LeaveCreditsTab = forwardRef(({ employeePersonalInformationId, isEditMode, showGeneralMessage }, ref) => {
  const {
    employeeLeaveCredits,
    currentYear, // ADDED
    isLockedByYear,
    selectedEmployeeLeaveCredit, setSelectedEmployeeLeaveCredit,
    updateLocalBalance, // Now available again
    executeSave,
    loading
  } = useEmployeeLeaveBalance({
    employeePersonalInformationId,
    showGeneralMessage
  });

  useEffect(() => {
    if (isLockedByYear === true && isEditMode === true) {
      showGeneralMessage("Cannot Edit this leave, already encashed this year.", false);
    }
  }, [isLockedByYear, isEditMode]);

  const [currentPage, setCurrentPage] = useState(1);
  const [isCreditsTabAddEditModalOpen, setIsCreditsTabAddEditModalOpen] = useState(false);

  const resetEmployeeLeaveCreditVariables = () => {
    setSelectedEmployeeLeaveCredit(null);
  }

  const handleEditEmployeeLeaveCreditModal = (item) => {
    setSelectedEmployeeLeaveCredit(item);
    setIsCreditsTabAddEditModalOpen(true);
  };

  const handleCloseEmployeeLeaveCreditModal = () => {
    setIsCreditsTabAddEditModalOpen(false);
    resetEmployeeLeaveCreditVariables();
  };

  const handleSave = (formData) => {
    // Optimistic UI update
    updateLocalBalance(formData.leaveTypeId, "totalLeaveCredit", formData.totalLeaveCredit);
    updateLocalBalance(formData.leaveTypeId, "remainingLeaveCredit", formData.remainingLeaveCredit);

    executeSave(formData);
  }

  const headers = [
    "Leave Type",
    "Total Credits",
    "Remaining",
    "Encashable",
    "Tax Treatment",
    ...(isEditMode && !isLockedByYear ? ["Actions"] : []),
  ];
  const accessors = [
    "leaveTypeDescription",
    "totalLeaveCredit",
    "remainingLeaveCredit",
    "isEncashable",
    "taxTreatmentName",
    ...(isEditMode && !isLockedByYear ? ["actions"] : []),
  ];

  const customCellRenderer = useCallback((row, accessor) => {
    if (accessor === "isEncashable") {
      return <div className="text-xs text-gray-500">{row.isEncashable ? `Yes (${row.maxEncashableDays} days)` : "No"}</div>;
    }

    if (accessor === "taxTreatment") {
      return <div className="text-xs text-gray-500">{row.taxTreatmentName || "—"}</div>;
    }
    return null;
  }, []);

  const actions = [
    {
      name: "Edit",
      // MODIFIED: Visual feedback if year is missing
      tooltip: !currentYear
        ? "Payroll Year not configured"
        : isLockedByYear
          ? "Locked: Credits already encashed or posted for this year"
          : "Edit Leave Credits",
      icon: <EditIcon fontSize="small" />,
      className: `${currentYear ? "bg-green-500 hover:bg-green-600" : "bg-gray-400 grayscale cursor-not-allowed"} flex items-center`,
      function: (item) => {
        if (!currentYear) return;
        handleEditEmployeeLeaveCreditModal(item);
      },
      visible: isEditMode && !isLockedByYear,
    }
  ];

  return (
    <>
      <div className="family-tab-body">
        <div className="employeesTab-table container">
          <CreateTable
            tableName={"EmployeeLeaveCredits"}
            headers={headers}
            accessors={accessors}
            data={employeeLeaveCredits}
            actions={actions}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            itemsPerPage={10}
            totalItems={employeeLeaveCredits?.length}
            customCellRenderer={customCellRenderer}
          />
        </div>
      </div>

      {isCreditsTabAddEditModalOpen && (
        <LeaveCreditsTabAddEditModal
          isOpen={isCreditsTabAddEditModalOpen}
          onClose={handleCloseEmployeeLeaveCreditModal}
          selectedItem={selectedEmployeeLeaveCredit}
          onSave={(formData) => {
            handleSave(formData);
            setIsCreditsTabAddEditModalOpen(false);
          }}
        />
      )}

      <LoadingSpinner loading={loading} />
    </>
  );
});